import { environment } from '../../environments/environment';

import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';

import { switchMap, mergeMap, catchError } from 'rxjs/operators';
import { Observable, of, from } from 'rxjs';

import { Series } from './../models/series';
import { Serie } from '../models/serie';
import { Student } from '../models/student';

import { StorageService } from './storage.service';
import { StudentService } from './student.service';
import { QuestionsService } from './questions.service';

import { Platform } from '@ionic/angular/standalone';

import { EasyDebugDecorator } from '../../app/decorators/easy-debug.decorator';
import { UserErrorHandlerService } from './user-error-handler.service';

import { Initializable } from './app-init.service';

@Injectable({
  providedIn: 'root',
})
@EasyDebugDecorator
export class SeriesService extends Series implements Initializable {
  private _seriesId: Array<Serie> = [];
  private _seriesSize = 0;
  public currentSerie: Serie = null;
  public errorSerie: Serie = null;

  constructor(
    private http: HttpClient,
    private storageService: StorageService,
    private studentService: StudentService,
    private questionsService: QuestionsService,
    private platform: Platform,
    private userErrorHandlerService: UserErrorHandlerService
  ) {
    super();
  }

  async init() {
    this.currentSerie = null;
    this.errorSerie = null;
    this.series = [];
    this.seriesList = [];
    this.individualSeries = [];
    this.individualSeriesWithQuestions = [];
    this.themes = [];
    return 'Series done';
  }

  // --- Inlined from RemoteSeries ---

  async getSeriesList(student: Student, local?: boolean): Promise<any> {
    if (!!this.seriesList && this.seriesList.length > 0) {
      return this.seriesList;
    }
    this.seriesList = await this.getSeries(student);
    return this.seriesList;
  }

  async getSerie(serieId, student: Student, withQuestions: boolean, local = false): Promise<any> {
    if (!!this.individualSeries && this.individualSeries.find((elt) => elt.id === serieId) && !withQuestions) {
      return this.individualSeries.find((elt) => elt.id === serieId);
    } else {
      let url: string;
      if (!!student && !!student.remoteId && student.remoteId !== 'GUEST') {
        url = `${environment.cdrBase}/v${environment.apiOptiVersion}/account/${student.remoteId}/series/${serieId}`;
      } else {
        url = `${environment.cdrBase}/v${environment.apiOptiVersion}/series/${serieId}`;
      }

      return this.http
        .get(url)
        .pipe(
          switchMap((dataSerie: any) => {
            if (!!dataSerie && !!dataSerie.data) {
              const serie = this.fillSerie(dataSerie.data);
              serie.questions = dataSerie.data.relationships.questions.data.map((elt) => elt.id);
              this.individualSeries.push(serie);
              return of(serie);
            }
            return of({
              errorCode: 'E301',
              errorMessage: 'remoteGetSerie Service failed',
              errorOriginal: 'No seriesData or no data in seriesData',
            });
          }),
          catchError((err) => {
            return of({
              errorCode: 'E301',
              errorMessage: 'remoteGetSerie Service failed',
              errorOriginal: err,
            });
          })
        )
        .toPromise();
    }
  }

  async getSeries(student: Student, local?: boolean): Promise<any> {
    if (!!this.series && this.series.length > 0) {
      return this.series;
    }
    let url: string;

    if (!!student && !!student.remoteId && student.remoteId !== 'GUEST') {
      url = `${environment.cdrBase}/v${environment.apiOptiVersion}/account/${student.remoteId}/series`;
    } else {
      url = `${environment.cdrBase}/v${environment.apiOptiVersion}/series`;
    }

    return this.http
      .get(url)
      .pipe(
        switchMap((seriesData: any) => {
          if (!!seriesData && !!seriesData.data) {
            const series = this.fillSeries(seriesData.data);
            this.series = series;
            return of(series);
          } else {
            return of({
              errorCode: 'E301',
              errorMessage: 'remoteGetSeries Service failed',
              errorOriginal: 'No seriesData or no data in seriesData',
            });
          }
        }),
        catchError((err) => {
          return of({
            errorCode: 'E301',
            errorMessage: 'remoteGetSeries Service failed',
            errorOriginal: err,
          });
        })
      )
      .toPromise();
  }

  async setSeries(series: Serie[], student: Student): Promise<Serie[]> {
    this.series = series;
    return this.series;
  }

  async setSerie(serie: Serie, student: Student): Promise<Serie> {
    if (!!this.individualSeries && !!this.individualSeries.find((elt) => elt.id === serie.id)) {
      this.individualSeries.splice(
        this.individualSeries.findIndex((elt) => elt.id === serie.id),
        1,
        serie
      );
    } else {
      this.individualSeries.push(serie);
    }
    if (!!this.series && this.series.find((elt) => elt.id === serie.id)) {
      this.series.splice(
        this.series.findIndex((elt) => elt.id === serie.id),
        1,
        serie
      );
    } else {
      this.series.push(serie);
    }
    return serie;
  }

  async setSeriesList(seriesList: Serie[], student: Student): Promise<any[]> {
    this.seriesList = seriesList;
    return this.seriesList;
  }

  async getThemes(student: Student, local = true): Promise<any> {
    if (!!this.themes && this.themes.length > 0 && !!local && local) {
      return this.themes;
    }
    let url: string;

    url = `${environment.cdrBase}/v${environment.apiVersion}/question/themes`;

    return this.http
      .get(url)
      .pipe(
        switchMap((themesData: any) => {
          if (!!themesData && !!themesData.data) {
            const themes = this.fillThemes(themesData.data);
            this.themes = themes;
            return of(themes);
          } else {
            return of({
              errorCode: 'E301',
              errorMessage: 'remoteGetThemes Service failed',
              errorOriginal: 'No themesData or no data in themesData',
            });
          }
        }),
        catchError((err) => {
          return of({
            errorCode: 'E301',
            errorMessage: 'remoteGetThemes Service failed',
            errorOriginal: err,
          });
        })
      )
      .toPromise();
  }

  async setThemes(themes: any[], student: Student): Promise<any[]> {
    this.themes = themes;
    return this.themes;
  }

  // --- End inlined from RemoteSeries ---

  async fillSeriesId(series: Array<any>, student: Student): Promise<any> {
    let scopedSerie = [];
    const newSeries = [];
    if (!!student && !!series && series.length > 0) {
      if (!this.studentService.student) {
        scopedSerie = series.filter((elt) => elt.attributes.scope === 'free');
      } else {
        scopedSerie = series;
      }
      this._seriesSize = !!scopedSerie ? scopedSerie.length : 0;
      this._seriesId = (await this.getSeriesList(student, true)) || [];

      if (!!series && series.length > 0) {
        for (const serie of series) {
          const lastSessions: string[] = [];
          const serieId = this._seriesId.find((elt) => elt.id === serie.id);
          const downloadState = !!serieId && !!serieId.downloadState ? serieId.downloadState : 'none';

          lastSessions.push(
            !!serie.attributes.last_session
              ? !!serie.attributes.last_session.training.data
                ? serie.attributes.last_session.training.data.id
                : null
              : null
          );
          lastSessions.push(
            !!serie.attributes.last_session
              ? !!serie.attributes.last_session.exam.data
                ? serie.attributes.last_session.exam.data.id
                : null
              : null
          );

          const newSerie = new Serie({
            id: !!serie.id ? serie.id : null,
            position:
              (!!serie.attributes && !!serie.attributes.position) ||
              (!!serie.attributes && serie.attributes.position === 0)
                ? serie.attributes.position
                : null,
            type: !!serie.attributes && !!serie.attributes.type ? serie.attributes.type : null,
            scope: !!serie.attributes && !!serie.attributes.scope ? serie.attributes.scope : null,
            downloadState: downloadState,
            updatedAt: !!serie.attributes && !!serie.attributes.updated_at ? serie.attributes.updated_at : null,
            lastSessions: lastSessions,
          });
          newSeries.push(newSerie);
          if (!!this._seriesId && !!this._seriesId.find((elt) => elt.id === serie.id)) {
            this._seriesId.splice(
              this._seriesId.findIndex((elt) => elt.id === serie.id),
              1,
              newSerie
            );
          } else {
            this._seriesId.push(newSerie);
          }
        }
      }
      await this.setSeriesList(this._seriesId, student);
    }
    return new Promise((resolve) => resolve(newSeries));
  }

  incorporateQuestions(serie: any): Observable<Serie> {
    if (!!serie && !!serie.id && !serie.erroCode) {
      return this.questionsService.fetchQuestionsBySerie(serie.id).pipe(
        switchMap((questions) => {
          if (!!questions && questions.length > 0) {
            const questionsId = [];
            for (const question of questions) {
              if (!!question.id) {
                questionsId.push(question.id);
              }
            }
            serie.questions = questionsId;
          }
          return of(serie);
        })
      );
    } else {
      return of(null);
    }
  }

  handleSeriesCalls(seriesId: Serie[], student: Student): Observable<Serie> {
    if (!!student && !!seriesId && seriesId.length > 0) {
      return from(seriesId).pipe(
        mergeMap((serieId) => {
          // À CHANGER
          if (
            !!serieId.scope &&
            (serieId.scope === '3free' ||
              serieId.scope === 'free' ||
              (!!student && !!student.status && student.status !== 'guest'))
          ) {
            return this.fetchSerie(serieId.id, student).pipe(
              switchMap((serie: Serie) => {
                return this.incorporateQuestions(serie);
              })
            );
          }
          return of(null);
        })
      );
    }
    return null;
  }

  fetchSerie(serieId: string, student: Student): Observable<any> {
    let url: string;
    if (!!serieId) {
      if (!!student && !!student.remoteId && student.remoteId !== 'GUEST') {
        url = `${environment.cdrBase}/v${environment.apiOptiVersion}/account/${student.remoteId}/series/${serieId}`;
      } else {
        url = `${environment.cdrBase}/v${environment.apiOptiVersion}/series/${serieId}`;
      }
      return this.http.get(url).pipe(
        switchMap((dataSerie: any) => {
          return of(this.fillSerie(dataSerie.data));
        }),
        catchError((err) => {
          return of({
            errorCode: 'E301',
            errorMessage: 'fetchSerie Service failed',
            errorOriginal: err,
          });
        })
      );
    }
    return null;
  }

  toSyncAll(series: Serie[], student: Student): Serie[] {
    const seriesToSync: Serie[] = [];
    if (!!student && !!series && series.length > 0) {
      for (const serie of series) {
        if (
          (!!serie.scope && (serie.scope === '3free' || serie.scope === 'free')) ||
          (!!student && !!student.status && student.status !== 'guest')
        ) {
          seriesToSync.push(
            new Serie({
              id: !!serie.id ? serie.id : null,
              position: !!serie.position ? serie.position : null,
              type: !!serie.type ? serie.type : null,
              scope: !!serie.scope ? serie.scope : null,
              downloadState: !!serie.downloadState ? serie.downloadState : 'none',
              updatedAt: !!serie.updatedAt ? serie.updatedAt : null,
              lastSessions: !!serie.lastSessions ? serie.lastSessions : null,
            })
          );
        }
      }
    }
    return seriesToSync;
  }

  pickSerieToSync(series: Serie[], localSeries: Serie[], student: Student): Serie[] {
    const seriesToSync: Serie[] = [];
    if (!!student && !!series && series.length > 0 && !!localSeries && localSeries.length > 0) {
      for (const serie of series) {
        if (!!serie.id) {
          const localSerie = this.getSerie(serie.id, student, false, true);
          if (!!localSerie) {
            // À CHANGER
            if (
              (!!serie.scope && (serie.scope === '3free' || serie.scope === 'free')) ||
              (!!student.status && student.status !== 'guest')
            ) {
              seriesToSync.push(
                new Serie({
                  id: serie.id,
                  position: !!serie.position ? serie.position : null,
                  type: !!serie.type ? serie.type : null,
                  scope: !!serie.scope ? serie.scope : null,
                  downloadState: !!serie.downloadState ? serie.downloadState : 'none',
                  updatedAt: !!serie.updatedAt ? serie.updatedAt : null,
                  lastSessions: !!serie.lastSessions ? serie.lastSessions : null,
                })
              );
            }
          }
        }
      }
    }
    return seriesToSync;
  }

  seriesNeedSync(series: Serie[], localSeries: Serie[], student: Student): Serie[] {
    let seriesToSync: Serie[] = [];
    if (!!student && !!series && series.length > 0 && !!localSeries && localSeries.length > 0) {
      seriesToSync = this.pickSerieToSync(series, localSeries, student);
    }
    return seriesToSync;
  }

  handleSeriesToSync(seriesToSync: Serie[], student: Student): Promise<Serie[]> {
    return new Promise((resolve) => {
      const series: Serie[] = [];
      let count = 0;
      if (!!student && !!seriesToSync && seriesToSync.length > 0) {
        this.handleSeriesCalls(seriesToSync, student).subscribe(async (serie) => {
          if (!!serie.id) {
            const serieToSync = seriesToSync.find((elt) => elt.id === serie.id);
            serie.downloadState = !!serieToSync && !!serieToSync.downloadState ? serieToSync.downloadState : 'none';
            const storageSerie = await this.setSerie(serie, student);
            if (!!storageSerie) {
              series.push(serie);
            }
            if (seriesToSync.length === count + 1) {
              const localSeries = await this.getSeriesList(student, true);
              if (!!localSeries && localSeries.length > 0) {
                for (const localSerie of localSeries) {
                  if (!!localSerie.id) {
                    const serieIndex = series.findIndex((elt) => elt.id === localSerie.id);
                    if (serieIndex !== -1) {
                      const localSerieIndex = localSeries.findIndex((elt) => elt.id === localSerie.id);
                      if (localSerieIndex !== -1) {
                        localSeries[localSerieIndex] = series[serieIndex];
                      }
                    }
                  }
                }
                await this.setSeriesList(localSeries, student);
              }
              resolve(series);
            }
            count++;
          }
          resolve(series);
        });
      }
      resolve(series);
    });
  }

  syncSeries(updatedAt: string, student: Student): Observable<Serie[]> {
    let localSeries: Serie[] = [];
    if (!!updatedAt && !!student) {
      return this.fetchSeries(updatedAt, student).pipe(
        switchMap(async (series) => {
          localSeries = await this.getSeriesList(student, true);
          if (!!series && series.length > 0 && !!localSeries && localSeries.length > 0) {
            const seriesToSync = this.seriesNeedSync(series, localSeries, student);
            if (!!seriesToSync && seriesToSync.length > 0) {
              return this.handleSeriesToSync(seriesToSync, student);
            }
          }
          return localSeries;
        })
      );
    }
    return of(localSeries);
  }

  fetchSeries(updatedAt: string, student: Student): Observable<Serie[]> {
    this._seriesId = [];
    let url: string;
    let query = '';

    if (!!updatedAt) {
      query = `?updated_at=${updatedAt}`;
      query = query.replace('+', '%2B');
      if (!!student && !!student.remoteId && student.remoteId !== 'GUEST') {
        url = `${environment.cdrBase}/v${environment.apiVersion}/account/${student.remoteId}/series${query}`;
      } else {
        url = `${environment.cdrBase}/v${environment.apiVersion}/series${query}`;
      }

      return this.http.get(url).pipe(
        switchMap(async (seriesData: any) => {
          if (!!seriesData && !!seriesData.data) {
            const series = await this.fillSeriesId(seriesData.data, student);
            return series;
          } else {
            return null;
          }
        }),
        catchError(async (err) => {
          this.userErrorHandlerService.addError({
            criticity: 20,
            service: 'fetchSeries',
            platform: 'both',
            data: err,
            errorCode: 'ssfss',
          });
          return null;
        })
      );
    }
    return of(null);
  }

  async syncThemes(student: Student): Promise<Array<string>> {
    if (!!student) {
      const questions = await this.questionsService.getQuestions(student, true);
      let themes = [];
      const resGetThemes = await this.getThemes(student);
      if (!!resGetThemes && !!resGetThemes.errorCode && resGetThemes.errorCode === 'E301') {
        // service failed
      } else {
        themes = resGetThemes;
      }
      if (!!themes && themes.length > 0) {
        return themes;
      }
      if (!!questions && questions.length > 0) {
        const themesName = Array.from(new Set(questions.map((item: any) => item.themes.join()))).filter((elt) => !!elt);
        if (!!themesName && themesName.length > 0) {
          for (const themeName of themesName) {
            const themeQuestions = questions.filter((elt) => elt.themes.includes(themeName));
            themes.push({
              name: themeName,
              questions: themeQuestions.map((elt) => elt.id) || [],
            });
          }
        }
        return await this.setThemes(themes, student);
      }
    }
    return [];
  }

  resetStats(): Observable<any> {
    const url = `${environment.token_auth_config.apiBase}/v3/sessions/reset`;

    return this.http.post(url, null).pipe(
      switchMap((data) => {
        return of(data);
      }),
      catchError(async (err) => {
        return {
          errorCode: 'E301',
          errorMessage: 'resetStats Service failed',
          errorOriginal: err,
        };
      })
    );
  }

  async generateThematicSerie(theme: string, student: Student): Promise<string[]> {
    const questions: string[] = [];
    if (!!student && !!theme) {
      let themes = [];
      const resGetThemes = await this.getThemes(student);
      if (!!resGetThemes && !!resGetThemes.errorCode && resGetThemes.errorCode === 'E301') {
        // service failed
      } else {
        themes = resGetThemes;
      }
      // toutes les questions du theme
      if (themes.length > 0) {
        let themePool = themes.find((elt) => elt.name === theme).questions;
        // liste de toutes les questions d'un theme
        let lQuestions = [];
        const resGetThemes2 = await this.getThemes(student, false);
        if (!!resGetThemes2 && !!resGetThemes2.errorCode && resGetThemes2.errorCode === 'E301') {
          // service failed
        } else {
          if (resGetThemes2.length > 0) {
            lQuestions = resGetThemes2.find((elt) => elt.name === theme).questions;
          }
        }
        let cpt = 0;
        let whileHasMadeInfiniteLoop = false;
        while (questions.length < 40) {
          let index = Math.floor(Math.random() * themePool.length);
          if (themePool.length === 1 && index > 0) {
            index = 0;
          }
          if (!questions.includes(themePool[index])) {
            questions.push(themePool[index]);
            themePool.splice(index, 1);
          }
          if (themePool.length === 0) {
            themePool = lQuestions;
          }
          cpt++;
          if (cpt > 600) {
            whileHasMadeInfiniteLoop = true;
            break;
          }
        }
        if (whileHasMadeInfiniteLoop) {
          return [];
        } else {
          themes.find((elt) => elt.name === theme).questions = themePool;
          themes = await this.setThemes(themes, student);
          return questions;
        }
      }
    }
    return new Promise((resolve) => resolve(questions));
  }

  createSerie(type: string, questions: string[], student: Student): Observable<any> {
    if (!!student && !!type && !!questions && questions.length > 0) {
      const url = `${environment.token_auth_config.apiBase}/v${environment.apiVersion}/account/${student.remoteId}/serie`;
      const data = {
        serie_type: `Cdr::${type}`,
        questions: questions,
      };

      return this.http.post(url, data).pipe(
        switchMap(async (serie: any) => {
          let newSerie = this.fillSerie(serie.data);
          if (!!newSerie) {
            newSerie.questions = questions;
            newSerie = await this.setSerie(newSerie, student);

            let series = await this.getSeriesList(student);
            if (!!series.errorCode && series.errorCode === 'E301') {
              series = [];
            }
            if (!!newSerie) {
              series.push(
                new Serie({
                  id: !!newSerie.id ? newSerie.id : null,
                  position: !!newSerie.position ? newSerie.position : null,
                  type: !!newSerie.type ? newSerie.type : null,
                  scope: !!newSerie.scope ? newSerie.scope : null,
                  theme: !!newSerie.theme ? newSerie.theme : null,
                  downloadState: !!newSerie.downloadState ? newSerie.downloadState : 'none',
                  updatedAt: !!newSerie.updatedAt ? newSerie.updatedAt : null,
                  lastSessions: !!newSerie.lastSessions ? newSerie.lastSessions : null,
                  nbQuestions: !!newSerie.nbQuestions ? newSerie.nbQuestions : null,
                })
              );
              await this.setSeriesList(series, student);
            }
          }
          return newSerie;
        }),
        catchError(async (err) => {
          return null;
        })
      );
    }
  }

  resetErrors(): Observable<any> {
    const url = `${environment.token_auth_config.apiBase}/v${environment.apiVersion}/error_serie`;
    return this.http.delete(url).pipe(
      switchMap(async (res: any) => {
        return res;
      }),
      catchError(async (err) => {
        return null;
      })
    );
  }

  fetchCrashTestData(): Observable<any> {
    const url = `${environment.token_auth_config.apiBase}/v3/cdr/sudden_death/survival_mode`;
    return this.http.get(url).pipe(
      switchMap(async (res: any) => {
        return res;
      }),
      catchError(async (err) => {
        return {
          errorCode: 'E301',
          errorMessage: 'fetchCrashTestData Service failed',
          errorOriginal: err,
        };
      })
    );
  }

  public get seriesSize() {
    return this._seriesSize;
  }
  public set seriesSize(value) {
    this._seriesSize = value;
  }
}
